import React, { useState, useRef, useEffect } from "react";
import { Globe, ArrowLeft, ArrowRight, RotateCcw, Sparkles, Loader2, Plus, X, Star, BookOpen, Send } from "lucide-react";
import { api, aiQuick } from "../api";

const HOME = "https://example.com";

const newTab = (url = HOME) => ({
  id: `t-${Date.now()}-${Math.floor(Math.random()*999)}`,
  url,
  history: [url],
  histIdx: 0,
  title: "New Tab",
  iframeUrl: url,
  pageText: "",
  canIframe: true,
  readerMode: false,
  loading: false,
});

export default function Browser() {
  const [tabs, setTabs] = useState([newTab()]);
  const [activeId, setActiveId] = useState(tabs[0].id);
  const [urlBar, setUrlBar] = useState(HOME);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiOut, setAiOut] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const iframeRef = useRef(null);

  const active = tabs.find(t => t.id === activeId) || tabs[0];

  useEffect(() => { setUrlBar(active.url); }, [activeId]);

  // Auto-fetch home page on first mount
  useEffect(() => {
    if (tabs[0] && !tabs[0].pageText) {
      navigate(tabs[0].url, tabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTab = (id, patch) => setTabs(ts => ts.map(t => t.id === id ? { ...t, ...patch } : t));

  const navigate = async (rawUrl, tabId = activeId) => {
    let url = rawUrl.trim();
    if (!url) return;
    // smart: if it doesn't look like a URL, search Google
    const looksLikeUrl = /\.[a-z]{2,}/i.test(url) || url.startsWith("http");
    if (!looksLikeUrl) url = `https://www.google.com/search?q=${encodeURIComponent(url)}`;
    if (!url.startsWith("http")) url = "https://" + url;
    setUrlBar(url);
    updateTab(tabId, { url, iframeUrl: url, loading: true, title: "Loading…" });
    try {
      const { data } = await api.post("/web/fetch", { url });
      const t = tabs.find(x => x.id === tabId);
      const history = t ? [...t.history.slice(0, t.histIdx + 1), data.url] : [data.url];
      updateTab(tabId, {
        title: data.title || data.url,
        pageText: data.text || "",
        canIframe: data.can_iframe,
        iframeUrl: data.url,
        url: data.url,
        history,
        histIdx: history.length - 1,
        loading: false,
        readerMode: !data.can_iframe,
      });
    } catch (e) {
      updateTab(tabId, { loading: false, title: "Failed to load", pageText: "Could not fetch this page." });
    }
  };

  const back = () => {
    if (active.histIdx <= 0) return;
    const idx = active.histIdx - 1;
    updateTab(active.id, { histIdx: idx });
    navigate(active.history[idx], active.id);
  };
  const fwd = () => {
    if (active.histIdx >= active.history.length - 1) return;
    const idx = active.histIdx + 1;
    updateTab(active.id, { histIdx: idx });
    navigate(active.history[idx], active.id);
  };
  const reload = () => navigate(active.url, active.id);

  const addTab = () => {
    const t = newTab(HOME);
    setTabs([...tabs, t]);
    setActiveId(t.id);
    setTimeout(() => navigate(HOME, t.id), 50);
  };
  const closeTab = (id) => {
    const next = tabs.filter(t => t.id !== id);
    if (next.length === 0) {
      const t = newTab();
      setTabs([t]); setActiveId(t.id);
      return;
    }
    setTabs(next);
    if (activeId === id) setActiveId(next[next.length - 1].id);
  };

  const askAI = async (mode, customQ) => {
    if (!active.pageText && !customQ) return;
    setAiBusy(true);
    setAiOut("");
    const ctx = active.pageText ? `Page title: ${active.title}\nURL: ${active.url}\nPage content:\n${active.pageText}\n\n` : "";
    const prompts = {
      summarize: `${ctx}Summarize this page in 5 concise bullet points.`,
      keypoints: `${ctx}Extract the 5 most important key points or facts.`,
      tldr: `${ctx}Give a 2-sentence TL;DR.`,
      ask: `${ctx}Question: ${customQ}`,
    };
    try {
      const r = await aiQuick(prompts[mode], "You are an AI browsing assistant. Be concise and accurate.");
      setAiOut(r.text);
    } catch { setAiOut("AI unavailable."); }
    setAiBusy(false);
  };

  return (
    <div className="h-full flex flex-col" data-testid="app-browser">
      {/* Tabs */}
      <div className="flex items-end px-2 pt-2 gap-1 bg-black/40 border-b border-white/10">
        {tabs.map(t => (
          <div
            key={t.id}
            data-testid={`browser-tab-${t.id}`}
            onClick={() => setActiveId(t.id)}
            className={`group flex items-center gap-2 px-3 py-1.5 rounded-t-md text-xs max-w-[180px] cursor-pointer ${
              activeId === t.id ? "bg-white/10 border-t border-x border-white/10" : "bg-white/3 hover:bg-white/6"
            }`}
          >
            <Globe size={11} className="text-white/60 shrink-0" />
            <span className="truncate flex-1">{t.title || "New Tab"}</span>
            <button onClick={(e) => { e.stopPropagation(); closeTab(t.id); }} className="opacity-50 group-hover:opacity-100">
              <X size={11} />
            </button>
          </div>
        ))}
        <button onClick={addTab} data-testid="browser-newtab" className="p-1 rounded hover:bg-white/10"><Plus size={12} /></button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b border-white/10 bg-black/30">
        <button data-testid="browser-back" onClick={back} className="btn-ghost p-1.5"><ArrowLeft size={14} /></button>
        <button data-testid="browser-forward" onClick={fwd} className="btn-ghost p-1.5"><ArrowRight size={14} /></button>
        <button data-testid="browser-reload" onClick={reload} className="btn-ghost p-1.5"><RotateCcw size={14} /></button>
        <input
          data-testid="browser-url"
          value={urlBar}
          onChange={(e) => setUrlBar(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && navigate(urlBar)}
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-xs outline-none focus:border-white/30"
          placeholder="Search or type URL"
        />
        <button
          data-testid="browser-reader"
          onClick={() => updateTab(active.id, { readerMode: !active.readerMode })}
          className={`btn-ghost p-1.5 ${active.readerMode ? "text-[#ff7a2e]" : ""}`}
          title="Reader Mode"
        >
          <BookOpen size={14} />
        </button>
        <button
          data-testid="browser-ai-toggle"
          onClick={() => setAiOpen(!aiOpen)}
          className={`btn-accent text-xs flex items-center gap-1 ${aiOpen ? "" : "opacity-90"}`}
        >
          <Sparkles size={12} /> AI
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative bg-white/3">
          {active.loading && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10 z-10">
              <div className="h-full bg-[#ff7a2e] animate-pulse w-1/2" />
            </div>
          )}
          {active.readerMode || !active.canIframe ? (
            <div className="absolute inset-0 overflow-y-auto p-8 text-sm leading-relaxed text-white/85" data-testid="browser-reader-content">
              <div className="text-2xl font-light mb-2 tracking-tight">{active.title}</div>
              <div className="text-[10px] text-white/40 mb-6 mono">{active.url}</div>
              <div className="whitespace-pre-wrap max-w-3xl">{active.pageText || "Loading…"}</div>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={active.iframeUrl}
              title={active.title}
              className="w-full h-full bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="browser-iframe"
            />
          )}
        </div>

        {aiOpen && (
          <div className="w-80 border-l border-white/10 bg-black/40 flex flex-col">
            <div className="px-3 py-2 border-b border-white/10 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
              <Sparkles size={12} color="#ff7a2e" /> AI Page Assistant
            </div>
            <div className="p-3 flex flex-col gap-2">
              <button data-testid="browser-ai-summarize" disabled={aiBusy} onClick={() => askAI("summarize")} className="btn-ghost text-xs flex items-center gap-1">
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize page
              </button>
              <button data-testid="browser-ai-keypoints" disabled={aiBusy} onClick={() => askAI("keypoints")} className="btn-ghost text-xs">Key points</button>
              <button data-testid="browser-ai-tldr" disabled={aiBusy} onClick={() => askAI("tldr")} className="btn-ghost text-xs">TL;DR</button>
              <div className="flex gap-1 mt-2">
                <input
                  data-testid="browser-ai-ask"
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && askAI("ask", aiInput) && setAiInput("")}
                  placeholder="Ask about this page…"
                  className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs"
                />
                <button onClick={() => { askAI("ask", aiInput); setAiInput(""); }} className="btn-accent text-xs"><Send size={11} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 text-xs whitespace-pre-wrap text-white/85 border-t border-white/10">
              {aiBusy && !aiOut ? <div className="text-white/40">Thinking…</div> : aiOut || <div className="text-white/40">Browse a page, then ask anything.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
